// RFC 4648 Base32, which is how a secret is written on both sides of the site: /keygen spells a random one in it and
// /otp reads one back. Kept here rather than in either page, since neither owns it and a second copy is a second
// alphabet to get wrong. /codec's is not this one — it carries three alphabets and a format picker to choose between
// them, where a secret is only ever the standard spelling.
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

const VALUES = new Map(Array.from(ALPHABET, (char, index) => [char, index] as const));

// Five bits at a time off the top of a rolling buffer. Padding is asked for rather than assumed: an `otpauth` URI and
// every authenticator that reads one leave it off, and a trailing `=` in a QR code is a character nobody needs.
export function encodeBase32(bytes: Uint8Array, padded = false): string {
  let out = "";
  let buffer = 0;
  let bits = 0;
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      out += ALPHABET[(buffer >> bits) & 31];
    }
  }
  if (bits > 0) out += ALPHABET[(buffer << (5 - bits)) & 31];
  if (padded) { while (out.length % 8 !== 0) out += "="; }
  return out;
}

// Lenient in the ways a secret is copied: authenticators print it in groups of four, some in lower case, and a URI
// leaves the padding off. Strict in the one way that matters, since a character nobody can decode is a typo and a
// secret quietly missing a byte computes a code that is wrong with nothing on screen to say why.
export function decodeBase32(text: string): Uint8Array<ArrayBuffer> {
  const chars = text.replace(/[\s-]/g, "").replace(/=+$/, "").toUpperCase();
  const out: number[] = [];
  let buffer = 0;
  let bits = 0;

  for (const char of chars) {
    const value = VALUES.get(char);
    if (value === undefined) throw new Error(`"${char}" is not a Base32 character`);
    buffer = (buffer << 5) | value;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push((buffer >> bits) & 0xff);
    }
  }
  // Anything left over should be padding bits only; a whole unused character means the input was cut short.
  if (bits >= 5) throw new Error("Base32 is truncated: it does not spell a whole number of bytes");
  return new Uint8Array(out);
}
